import React from "react"
import styled from "@emotion/styled"
import Section from "src/components/Section"
import Grid, { Container } from "src/components/Grid"
import TextLockup from "src/components/TextLockup"
import ScrollEntrance from "src/components/ScrollEntrance"
import Image from "src/components/Image"
import { Card } from "src/styles/globals"
import { globals, typography, colors, util, mq } from "src/styles"

const Wrapper = styled(Section)`
  text-align: center;
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
  `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
`

const IntroText = styled(Container)`
  ${globals.verticalSpacing("margin-bottom", 0.4)}
  h1, h2 {
    max-width: 12em;
  }
  h3 {
    max-width: 18em;
  }
  h4,
  h5 {
    max-width: 24em;
  }
  h6 {
    max-width: 16em;
  }
  p {
    max-width: 36em;
  }
  ol,
  ul,
  dl,
  blockquote {
    max-width: 32em;
  }
`

const BottomActions = styled(Container)`
  ${globals.verticalSpacing("margin-top", 0.5)}
`

const DetailCard = styled(Card)`
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  background: ${colors.white};

  .eyebrow {
    margin-top: -0.2em;
  }
  p {
    max-width: 38em;
  }
`

const CardList = styled(TextLockup)`
  margin-top: 1.5em;
  ul {
    padding-left: 1.1em;
    width: 100%;
    ${mq.mediumAndUp} {
      columns: 2;
      column-gap: 40px;
    }
    li {
      &::marker {
        color: ${colors.sky};
      }
    }
    p {
      ${typography.bodySmall}
      color: ${colors.textColor};
      margin: 0 0 .5em;
      max-width: 24em;
    }
  }
`

const CardImageWrapper = styled.div`
  .gatsby-image-wrapper {
    border-top-left-radius: ${globals.cardBorderRadius};
    border-top-right-radius: ${globals.cardBorderRadius};
    img {
      margin: 0;
      border-top-left-radius: ${globals.cardBorderRadius};
      border-top-right-radius: ${globals.cardBorderRadius};
    }
  }
`

const CardInner = styled.div`
  flex-grow: 1;
  ${util.responsiveStyles("padding", 34, 26, 22, 20)}
  ${util.responsiveStyles("padding-right", 40, 30, 24, 20)}
  ${util.responsiveStyles("padding-bottom", 40, 30, 24, 20)}
  ${util.responsiveStyles("padding-left", 40, 30, 24, 20)}
`

const DetailCards = ({
  className,
  nextTheme,
  prevTheme,
  theme,
  spacing,
  isFirstSection,
  introText,
  cardOne,
  cardTwo,
  actions,
  id,
  hideMobile,
  hideDesktop,
}) => {
  return (
    <Wrapper
      className={className}
      prevTheme={prevTheme}
      setTheme={theme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      {introText && introText?._rawText && (
        <IntroText>
          <TextLockup
            eyebrow={introText.eyebrow}
            newStyle={introText?.newStyle}
            text={introText._rawText}
            alignment="center"
          />
        </IntroText>
      )}
      <Grid
        small="m [1] m"
        medium="1 [12] 1"
        large="1 [6] m [6] 1"
        vAlign="stretch"
        rowGap="20px"
        as={ScrollEntrance}
      >
        <DetailCard>
          {cardOne?.image?.asset?.gatsbyImageData && (
            <CardImageWrapper>
              <Image image={cardOne.image.asset.gatsbyImageData} />
            </CardImageWrapper>
          )}
          <CardInner>
            <TextLockup
              eyebrow={cardOne.eyebrow}
              headlineSize={cardOne.headingSize ? cardOne.headingSize : "h3"}
              headline={cardOne.headline}
              newStyle={false}
              text={cardOne?._rawText}
              transitionIn={false}
            />
            <CardList
              newStyle={false}
              text={cardOne._rawList}
              actions={cardOne.cardActions}
              transitionIn={false}
            />
          </CardInner>
        </DetailCard>
        <DetailCard>
          {cardTwo?.image?.asset?.gatsbyImageData && (
            <CardImageWrapper>
              <Image image={cardTwo.image.asset.gatsbyImageData} />
            </CardImageWrapper>
          )}
          <CardInner>
            <TextLockup
              eyebrow={cardTwo.eyebrow}
              headline={cardTwo.headline}
              text={cardTwo?._rawText}
              headlineSize={cardTwo.headingSize ? cardTwo.headingSize : "h3"}
              transitionIn={false}
            />
            <CardList
              newStyle={false}
              text={cardTwo._rawList}
              actions={cardTwo.cardActions}
              transitionIn={false}
            />
          </CardInner>
        </DetailCard>
      </Grid>
      {actions && actions?.length > 0 && (
        <BottomActions>
          <TextLockup actions={actions} />
        </BottomActions>
      )}
    </Wrapper>
  )
}

export default DetailCards
